import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const Cart = ({ cart, updateCart }) => {
  const navigate = useNavigate();

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(productId);
      return;
    }

    const newCart = cart.map(item => 
      item.id === productId ? { ...item, quantity: newQuantity } : item
    );
    updateCart(newCart);
  };

  const removeItem = (productId) => {
    const newCart = cart.filter(item => item.id !== productId);
    updateCart(newCart);
    toast.success('Item removed from cart');
  };

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  if (cart.length === 0) {
    return (
      <div className="pt-24 pb-16 px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto"
        >
          <h1 className="text-5xl font-black mb-6" data-testid="empty-cart-title">YOUR CART IS EMPTY</h1>
          <p className="text-white/60 mb-8">Start adding some amazing products!</p>
          <Button 
            onClick={() => navigate('/shop')} 
            data-testid="shop-now-button"
            className="bg-[#FF4500] hover:bg-[#FF4500]/90 uppercase tracking-widest"
          >
            Shop Now
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-5xl mx-auto"
      >
        <h1 className="text-5xl font-black mb-12" data-testid="cart-title">SHOPPING CART</h1>

        <div className="space-y-4 mb-8">
          {cart.map((item, index) => (
            <div 
              key={item.id} 
              data-testid={`cart-item-${index}`}
              className="flex gap-6 p-6 border border-white/10 hover:border-white/20 transition-all"
            >
              <div className="w-24 h-24 border border-white/10 overflow-hidden flex-shrink-0">
                {item.images && item.images.length > 0 ? (
                  <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-white/5"></div>
                )}
              </div>

              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2" data-testid={`cart-item-name-${index}`}>{item.name}</h3>
                <p className="text-[#FF4500] font-bold" data-testid={`cart-item-price-${index}`}>R{item.price.toFixed(2)}</p>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    data-testid={`decrease-quantity-${index}`}
                    size="icon"
                    variant="outline"
                    className="border-white/20 hover:bg-white/10 h-8 w-8"
                  >
                    <Minus size={16} />
                  </Button>
                  <span className="w-8 text-center font-bold" data-testid={`cart-item-quantity-${index}`}>{item.quantity}</span>
                  <Button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    data-testid={`increase-quantity-${index}`}
                    size="icon"
                    variant="outline"
                    className="border-white/20 hover:bg-white/10 h-8 w-8"
                  >
                    <Plus size={16} />
                  </Button>
                </div>

                <Button
                  onClick={() => removeItem(item.id)}
                  data-testid={`remove-item-${index}`}
                  size="icon"
                  variant="outline"
                  className="border-red-500/50 hover:bg-red-500/20 text-red-500 h-8 w-8"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="flex justify-between items-center mb-8">
            <span className="text-2xl font-bold">Total</span>
            <span className="text-3xl font-black text-[#FF4500]" data-testid="cart-total">R{total.toFixed(2)}</span>
          </div>

          <Button
            onClick={() => navigate('/checkout')}
            data-testid="proceed-to-checkout"
            className="w-full bg-[#FF4500] hover:bg-[#FF4500]/90 text-white uppercase tracking-widest font-bold px-8 py-6 text-lg neon-glow-hover"
          >
            Proceed to Checkout
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default Cart;